import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Example component demonstrating the dialog pre-rendering pattern.
 * This shows how to eliminate jitter when dialogs contain dynamic content.
 *
 * NEW: Enhanced with adaptive timeouts and resize animations for async content.
 */

// Simulated async component that changes size during loading
const AsyncContent: React.FC = () => {
	const [isLoaded, setIsLoaded] = React.useState(false);

	React.useEffect(() => {
		// Simulate loading delay
		const timer = setTimeout(() => setIsLoaded(true), 1500);
		return () => clearTimeout(timer);
	}, []);

	if (!isLoaded) {
		return (
			<div className="flex items-center justify-center py-4">
				<Loader2 className="w-4 h-4 animate-spin mr-2" />
				Loading content...
			</div>
		);
	}

	return (
		<div className="space-y-6 p-4">
			<div className="grid grid-cols-2 gap-4">
				<div>
					<label className="text-sm font-medium">Field 1</label>
					<input className="w-full p-2 border rounded mt-1" placeholder="Enter value" />
				</div>
				<div>
					<label className="text-sm font-medium">Field 2</label>
					<input className="w-full p-2 border rounded mt-1" placeholder="Enter value" />
				</div>
			</div>
			<div>
				<label className="text-sm font-medium">Description</label>
				<textarea
					className="w-full p-2 border rounded mt-1 h-24"
					placeholder="Enter detailed description..."
				/>
			</div>
			<div className="grid grid-cols-3 gap-2">
				{Array.from({ length: 6 }).map((_, i) => (
					<Button key={i} variant="outline" size="sm">
						Option {i + 1}
					</Button>
				))}
			</div>
		</div>
	);
};

interface ExampleDialogProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	enablePreRender?: boolean;
}

// Dialog WITHOUT pre-rendering (will show jitter)
export const JitteryDialog: React.FC<ExampleDialogProps> = ({
	isOpen,
	onClose,
	title
}) => (
	<Dialog open={isOpen} onOpenChange={onClose}>
		<DialogContent enablePreRender={false}>
			<DialogHeader>
				<DialogTitle>{title}</DialogTitle>
			</DialogHeader>
			<React.Suspense fallback={
				<div className="flex items-center justify-center py-4">
					<Loader2 className="w-4 h-4 animate-spin mr-2" />
					Loading...
				</div>
			}>
				<AsyncContent />
			</React.Suspense>
		</DialogContent>
	</Dialog>
);

// Dialog WITH pre-rendering (no jitter)
export const SmoothDialog: React.FC<ExampleDialogProps> = ({
	isOpen,
	onClose,
	title
}) => (
	<Dialog open={isOpen} onOpenChange={onClose}>
		<DialogContent enablePreRender={true}>
			<DialogHeader>
				<DialogTitle>{title}</DialogTitle>
			</DialogHeader>
			<React.Suspense fallback={
				<div className="flex items-center justify-center py-4">
					<Loader2 className="w-4 h-4 animate-spin mr-2" />
					Loading...
				</div>
			}>
				<AsyncContent />
			</React.Suspense>
		</DialogContent>
	</Dialog>
);

// Simple test dialog without pre-rendering
export const SimpleDialog: React.FC<ExampleDialogProps> = ({
	isOpen,
	onClose,
	title
}) => (
	<Dialog open={isOpen} onOpenChange={onClose}>
		<DialogContent enablePreRender={false}>
			<DialogHeader>
				<DialogTitle>{title}</DialogTitle>
			</DialogHeader>
			<div className="p-4">
				<p>This is a simple dialog without pre-rendering to test basic functionality.</p>
			</div>
		</DialogContent>
	</Dialog>
);

// Demo component to test both dialogs
export const DialogPreRenderDemo: React.FC = () => {
	const [showJittery, setShowJittery] = React.useState(false);
	const [showSmooth, setShowSmooth] = React.useState(false);
	const [showSimple, setShowSimple] = React.useState(false);
	const [showAsyncContent, setShowAsyncContent] = React.useState(false);

	return (
		<div className="space-y-4 p-6">
			<h2 className="text-xl font-semibold">Dialog Pre-Rendering Demo</h2>
			<p className="text-muted-foreground">
				Compare dialogs with and without pre-rendering to see the difference in animation smoothness.
			</p>

			<div className="grid grid-cols-2 gap-4">
				<Button
					onClick={() => setShowSimple(true)}
					variant="outline"
				>
					Simple Dialog (Test Basic Function)
				</Button>
				<Button
					onClick={() => setShowJittery(true)}
					variant="destructive"
				>
					Show Jittery Dialog (No Pre-render)
				</Button>
				<Button
					onClick={() => setShowSmooth(true)}
					variant="default"
				>
					Show Smooth Dialog (With Pre-render)
				</Button>
				<Button
					onClick={() => setShowAsyncContent(true)}
					className="bg-green-600 hover:bg-green-700 text-white"
				>
					NEW: Async Content Dialog
				</Button>
			</div>

			<SimpleDialog
				isOpen={showSimple}
				onClose={() => setShowSimple(false)}
				title="Simple Dialog Test"
			/>

			<JitteryDialog
				isOpen={showJittery}
				onClose={() => setShowJittery(false)}
				title="Jittery Dialog Example"
			/>

			<SmoothDialog
				isOpen={showSmooth}
				onClose={() => setShowSmooth(false)}
				title="Smooth Dialog Example"
			/>

			<AsyncContentDialog
				isOpen={showAsyncContent}
				onClose={() => setShowAsyncContent(false)}
				title="Async Content Dialog (Enhanced)"
			/>
		</div>
	);
};

// NEW: Async content dialog with enhanced pre-rendering
const AsyncContentDialog: React.FC<ExampleDialogProps> = ({
	isOpen,
	onClose,
	title
}) => {
	const [contentPhase, setContentPhase] = React.useState<'loading' | 'partial' | 'complete'>('loading');

	React.useEffect(() => {
		if (isOpen) {
			setContentPhase('loading');

			// Simulate progressive content loading
			setTimeout(() => setContentPhase('partial'), 800);
			setTimeout(() => setContentPhase('complete'), 2000);
		}
	}, [isOpen]);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				enablePreRender={true}
				preRenderTimeout={600}
				size="md"
			>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					{contentPhase === 'loading' && (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="w-6 h-6 animate-spin mr-2" />
							Loading content...
						</div>
					)}

					{contentPhase === 'partial' && (
						<div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium">Basic Info</label>
									<input className="w-full p-2 border rounded mt-1" placeholder="Loaded..." />
								</div>
								<div>
									<label className="text-sm font-medium">Status</label>
									<input className="w-full p-2 border rounded mt-1" placeholder="Active" />
								</div>
							</div>
							<div className="flex items-center justify-center py-4">
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
								Loading additional fields...
							</div>
						</div>
					)}

					{contentPhase === 'complete' && (
						<div className="space-y-6 animate-in slide-in-from-top-2 duration-300">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium">Name</label>
									<input className="w-full p-2 border rounded mt-1" placeholder="John Doe" />
								</div>
								<div>
									<label className="text-sm font-medium">Email</label>
									<input className="w-full p-2 border rounded mt-1" placeholder="john@example.com" />
								</div>
							</div>
							<div>
								<label className="text-sm font-medium">Description</label>
								<textarea
									className="w-full p-2 border rounded mt-1 h-24"
									placeholder="All content loaded! Notice how the dialog smoothly resizes as content appears."
								/>
							</div>
							<div className="grid grid-cols-4 gap-2">
								{Array.from({ length: 8 }).map((_, i) => (
									<Button key={i} variant="outline" size="sm">
										Action {i + 1}
									</Button>
								))}
							</div>
							<div className="p-4 bg-green-50 border border-green-200 rounded-lg">
								<p className="text-sm text-green-800">
									✅ All content loaded! The dialog should have smoothly animated to accommodate the growing content.
								</p>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
